"use client";
import { ArrowDown2 } from "iconsax-reactjs";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ComponentPropsWithoutRef } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { XIcon } from "@/components/ui/XIcon";
import { cn } from "@/lib/utils";
import { Spinner } from "../spinner";

// ----------------------------------------------------------------------

type SelectOption = {
  label: string;
  value: string;
};

interface CustomRHFMultiSelectProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentPropsWithoutRef<typeof Controller>,
    "name" | "control" | "render"
  > {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  options: SelectOption[];
  emptyText?: string;
  placeholder?: string;
  triggerClassName?: string;
  loading?: boolean;
}

export default function CustomRHFMultiSelect<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  options,
  emptyText,
  placeholder,
  triggerClassName,
  loading = false,
  ...other
}: CustomRHFMultiSelectProps<TFieldValues>) {
  const { control } = useFormContext();
  const t = useTranslations("common.form");

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || []}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const selectedValues = (field.value || []) as SelectOption[];
        const selectedValueStrings = selectedValues.map((item) => item.value);

        const handleToggle = (option: SelectOption) => {
          const currentValues = selectedValues;
          const isSelected = selectedValueStrings.includes(option.value);

          if (isSelected) {
            // Remove the option
            const newValues = currentValues.filter(
              (item) => item.value !== option.value,
            );
            field.onChange(newValues);
          } else {
            // Add the option
            field.onChange([...currentValues, option]);
          }
        };

        const handleRemoveBadge = (
          e: React.MouseEvent,
          optionToRemove: SelectOption,
        ) => {
          e.stopPropagation();
          const newValues = selectedValues.filter(
            (item) => item.value !== optionToRemove.value,
          );
          field.onChange(newValues);
        };

        return (
          <div className={cn("space-y-2 w-full", containerClass)}>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="bordered"
                  colors="secondary"
                  disabled={loading}
                  radius="xs"
                  className={cn(
                    "w-full justify-between h-9 border-stroke-light bg-transparent px-2",
                    !selectedValues.length && "text-muted-foreground",
                    error && "border-semantic-error",
                    triggerClassName,
                  )}
                >
                  {loading ? (
                    <Spinner className="h-4 w-4 text-text-secondary ms-auto" />
                  ) : (
                    <>
                      <span className="truncate line-clamp-1 text-muted-foreground">
                        {selectedValues.length > 0
                          ? t("selectedCount", { count: selectedValues.length })
                          : placeholder || emptyText}
                      </span>
                      <ArrowDown2 size={16} className="text-primary" />
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-(--radix-popover-trigger-width) p-0 bg-bg-secondary rounded-md border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]"
                align="end"
              >
                <div className="max-h-60 overflow-auto p-2">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner className="h-5 w-5 text-text-secondary" />
                    </div>
                  ) : options.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <span className="text-sm text-text-secondary">
                        {emptyText}
                      </span>
                    </div>
                  ) : (
                    options.map((option) => {
                      const isSelected = selectedValueStrings.includes(
                        option.value,
                      );
                      return (
                        <div
                          key={option.value}
                          className={cn(
                            "flex items-center gap-2 rounded-xs px-2 py-1.5 hover:bg-bg-tertiary/60 cursor-pointer",
                          )}
                          onClick={() => handleToggle(option)}
                        >
                          {isSelected ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <span className="h-4 w-4" />
                          )}
                          <span className="text-sm font-medium flex-1">
                            {option.label}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedValues.map((option) => (
                  <Badge
                    key={option.value}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 pr-1 pl-1 py-0.5 text-xs bg-transparent"
                  >
                    <span className="truncate max-w-[100px] mt-0.5">
                      {option.label}
                    </span>
                    <button
                      type="button"
                      className="flex items-center justify-center hover:bg-bg-tertiary rounded-xs p-0.5 transition-colors"
                      onClick={(e) => handleRemoveBadge(e, option)}
                    >
                      <XIcon size={16} className="text-text-secondary" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {!!helperText && (
              <p className="text-xs leading-tight text-muted-foreground ml-1.5">
                {helperText}
              </p>
            )}
            {!!error && (
              <p className="text-xs leading-tight text-semantic-error ml-1.5">
                {error?.message}
              </p>
            )}
          </div>
        );
      }}
      {...other}
    />
  );
}
